import { db, storage } from '../config/firebase';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { BottledDrink } from '../types/bottledDrink';
import { Dish } from '../types/dish';

const COLLECTION_NAME = 'bottledDrinks';

// Helper function to check if URL is a Firebase Storage URL
const isFirebaseStorageUrl = (url: string): boolean => {
    try {
        return url.includes('firebasestorage.googleapis.com') || 
               url.includes('storage.googleapis.com') ||
               url.startsWith('gs://');
    } catch {
        return false;
    }
};

export const bottledDrinkService = {
    async addBottledDrink(drink: BottledDrink, imageFiles: File[]) {
        try {
            const imageUrls = await Promise.all(
                imageFiles.map(async (file) => {
                    const storageRef = ref(storage, `bottled-drinks/${Date.now()}_${file.name}`);
                    const uploadResult = await uploadBytes(storageRef, file);
                    return getDownloadURL(uploadResult.ref);
                })
            );

            const docRef = await addDoc(collection(db, COLLECTION_NAME), {
                ...drink,
                coffeeId: '', // Set default value for coffeeId
                images: imageUrls,
                createdAt: new Date(),
                updatedAt: new Date()
            });

            return { id: docRef.id, ...drink, images: imageUrls };
        } catch (error) {
            throw new Error('Không thể thêm đồ uống: ' + error);
        }
    },

    async getBottledDrinkById(id: string) {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() } as BottledDrink;
            }
            return null;
        } catch (error) {
            throw new Error('Không thể lấy thông tin đồ uống: ' + error);
        }
    },

    async updateBottledDrink(id: string, drink: Partial<BottledDrink>, newImageFiles?: File[], oldImageUrls?: string[]) {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const currentDoc = await getDoc(docRef);

            if (!currentDoc.exists()) {
                throw new Error('Không tìm thấy đồ uống');
            }

            let updateData = {
                ...drink,
                coffeeId: '', 
                updatedAt: new Date()
            };

            if (oldImageUrls && oldImageUrls.length > 0) {
                console.log('oldImageUrls:', oldImageUrls);
                const currentImageUrls = currentDoc.data().images || [];
                console.log('currentImageUrls:', currentImageUrls);
                
                const imagesToDelete = currentImageUrls.filter((url: string) => !oldImageUrls.includes(url));
                console.log('imagesToDelete:', imagesToDelete);
                
                // Xóa từng ảnh cũ với kiểm tra URL hợp lệ
                let deletedOldCount = 0;
                let skippedOldCount = 0;
                
                for (const url of imagesToDelete) {
                    if (isFirebaseStorageUrl(url)) {
                        try {
                            const imageRef = ref(storage, url);
                            await deleteObject(imageRef);
                            deletedOldCount++;
                            console.log(`Successfully deleted old image: ${url}`);
                        } catch (error) {
                            console.error(`Error deleting old image ${url}:`, error);
                        }
                    } else {
                        skippedOldCount++;
                        console.log(`Skipped non-Firebase Storage URL: ${url}`);
                    }
                }

                if (imagesToDelete.length > 0) {
                    console.log(`Update cleanup for bottled drink ID ${id}: ${deletedOldCount} old images deleted, ${skippedOldCount} non-Firebase URLs skipped`);
                }

                updateData = {
                    ...updateData,
                    images: oldImageUrls
                };
            }

            if (newImageFiles?.length) {
                const newImageUrls = await Promise.all(
                    newImageFiles.map(async (file) => {
                        const storageRef = ref(storage, `bottled-drinks/${Date.now()}_${file.name}`);
                        const uploadResult = await uploadBytes(storageRef, file);
                        return getDownloadURL(uploadResult.ref);
                    })
                );

                updateData = {
                    ...updateData,
                    images: [...(updateData.images || []), ...newImageUrls]
                };
            }

            await updateDoc(docRef, updateData);
            return { id, ...updateData };
        } catch (error) {
            throw new Error('Không thể cập nhật đồ uống: ' + error);
        }
    },

    async deleteBottledDrink(id: string) {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                const imageUrls = data.images || [];
                
                // Xóa tất cả ảnh từ Firebase Storage
                let deletedCount = 0;
                let skippedCount = 0;
                
                for (const url of imageUrls) {
                    // Chỉ xóa nếu là Firebase Storage URL
                    if (isFirebaseStorageUrl(url)) {
                        try {
                            const imageRef = ref(storage, url);
                            await deleteObject(imageRef);
                            deletedCount++;
                            console.log(`Successfully deleted image: ${url}`);
                        } catch (error) {
                            console.error(`Error deleting image ${url}:`, error);
                            // Tiếp tục xử lý dù có lỗi khi xóa ảnh
                        }
                    } else {
                        skippedCount++;
                        console.log(`Skipped non-Firebase Storage URL: ${url}`);
                    }
                }

                // Ghi log để theo dõi số lượng ảnh đã xóa
                console.log(`Storage cleanup for bottled drink ID ${id}: ${deletedCount} images deleted, ${skippedCount} non-Firebase URLs skipped`);
            }

            // Xóa document từ Firestore
            await deleteDoc(docRef);
            console.log(`Successfully deleted bottled drink document with ID: ${id}`);
        } catch (error) {
            console.error(`Error deleting bottled drink with ID ${id}:`, error);
            throw new Error('Không thể xóa đồ uống: ' + error);
        }
    },

    async getAllBottledDrinks() {
        const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as BottledDrink[];
    },

    async syncPricesWithDishes(dishes: Dish[]) {
        try {
            if (!dishes || dishes.length === 0) {
                return {
                    success: false,
                    message: 'Không có dữ liệu món ăn',
                    updatedCount: 0,
                    updatedDrinks: []
                };
            }

            // Create a map of dishes by code for quick lookup
            const dishMap = new Map<string, Dish>();
            dishes.forEach(dish => {
                if (dish.code) {
                    dishMap.set(dish.code, dish);
                }
            });

            if (dishMap.size === 0) {
                return {
                    success: false,
                    message: 'Không có mã món ăn hợp lệ để đồng bộ',
                    updatedCount: 0,
                    updatedDrinks: []
                };
            }

            // Get all bottled drinks
            const drinks = await this.getAllBottledDrinks();
            
            if (!drinks || drinks.length === 0) {
                return {
                    success: false,
                    message: 'Không có dữ liệu đồ uống',
                    updatedCount: 0,
                    updatedDrinks: []
                };
            }
            
            // Track updated drinks and sync details for return value
            const updatedDrinks: BottledDrink[] = [];
            const syncDetails: { 
                drinkName: string, 
                productCode: string, 
                oldPrice: number, 
                newPrice: number 
            }[] = [];
            
            // Update each drink that has a matching product code
            for (const drink of drinks) {
                if (drink.productCode && dishMap.has(drink.productCode)) {
                    const matchingDish = dishMap.get(drink.productCode);
                    
                    // Check if drink has volumes to update
                    if (drink.volumes && drink.volumes.length > 0) {
                        // By default, update the first volume with the dish price
                        const updatedVolumes = drink.volumes.map((vol, index) => {
                            if (index === 0) {
                                // Track sync details for reporting
                                syncDetails.push({
                                    drinkName: drink.name,
                                    productCode: drink.productCode!,
                                    oldPrice: vol.price,
                                    newPrice: matchingDish!.price
                                });
                                
                                return {
                                    volume: vol.volume,
                                    price: matchingDish!.price
                                };
                            }
                            return vol; // Keep other volume prices as is
                        });
                        
                        // Update the drink in the database
                        await this.updateBottledDrink(drink.id!, {
                            volumes: updatedVolumes,
                            updatedAt: new Date()
                        });
                        
                        // Add to updated drinks list
                        updatedDrinks.push({
                            ...drink,
                            volumes: updatedVolumes
                        });
                    }
                }
            }
            
            return {
                success: true,
                message: updatedDrinks.length > 0 
                    ? `Đã đồng bộ ${updatedDrinks.length} đồ uống thành công` 
                    : 'Không có đồ uống nào được đồng bộ. Kiểm tra lại mã đồ uống của bạn.',
                updatedCount: updatedDrinks.length,
                updatedDrinks,
                syncDetails
            };
        } catch (error) {
            console.error('Error syncing prices with dishes:', error);
            throw new Error('Không thể đồng bộ giá với món ăn: ' + error);
        }
    }
};